import React, { useEffect, useState, createContext, PropsWithChildren, Context } from 'react';
import mqtt, { IClientOptions, MqttClient } from 'mqtt';
// import { Buffer } from 'buffer';

type QoS = 0 | 1 | 2

type mqtt_config_t = {
    mqtt_uri: string,
    username: string,
    password: string,
    topic: string
}

interface publish_context_t {
  topic: string;
  qos: QoS;
  payload: string;
}

interface subscription_t {
  topic: string;
  qos: QoS;
}


export interface mqtt_context_t {
  isSubed: boolean;
  payload: Record<string, any>;
  connectStatus: string;
  mqttPublish: (context: publish_context_t) => void;
  mqttSub: (subscription: subscription_t) => void;
}

 
const is_local_dev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

// derive mqtt uri from api base url if available, otherwise use default
const get_mqtt_uri = () => {
    if (is_local_dev) {
        return "ws://localhost:9001"
    }

    // derive the broker from the api we are actually talking to
    const api_base = import.meta.env.VITE_API_BASE_URL
    if (api_base) {
        // VITE_API_BASE_URL is absolute in every deployed environment, but the
        // checked-in .env uses a relative "/api/v1/" for the local dev proxy -
        // and new URL() throws on that, which used to take the whole page down.
        try {
            const url = new URL(api_base, window.location.origin)
            return `wss://${url.hostname}/wsmsg`
        } catch {
            // fall through to the host we were served from
        }
    }

    // same host as the app, rather than a hardcoded environment
    return `wss://${window.location.hostname}/wsmsg`
}

const mqtt_uri = get_mqtt_uri()

// get mqtt credentials from environment or use defaults
const mqtt_username = import.meta.env.VITE_MQTT_USERNAME || "eio_backend"
const mqtt_password = import.meta.env.VITE_MQTT_PASSWORD || "S5XYTfohUMIaEf8H"

export const mqtt_config : mqtt_config_t = {
    mqtt_uri,
    username : mqtt_username,
    password : mqtt_password,
    topic : "eres/worker"
}


export const MqttContext : Context<mqtt_context_t> = createContext<mqtt_context_t>(undefined as unknown as mqtt_context_t)


const MqttInitializer = ({children} : PropsWithChildren<{}>) => {
  const [client, setClient] = useState<MqttClient | null>(null);
  const [isSubed, setIsSub] = useState(false);
  const [payload, setPayload] = useState<Record<string, string>>({});
  const [connectStatus, setConnectStatus] = useState('Connect');

  const mqttConnect = () => {
    setConnectStatus('Connecting');

    const url = mqtt_config.mqtt_uri;

    const options : IClientOptions = {
      keepalive: 30,
      protocolId: 'MQTT',
      protocolVersion: 4,
      clean: true,
      reconnectPeriod: 1000,
      connectTimeout: 30 * 1000,
      will: {
        topic: 'WillMsg',
        payload: 'Connection Closed abnormally..!' as any,
        qos: 0,
        retain: false
      },
      rejectUnauthorized: false
    };
    options.clientId = `mqttjs_ + ${Math.random().toString(16).substr(2, 8)}`;
    options.username = mqtt_config.username;
    options.password = mqtt_config.password;
    setClient(mqtt.connect(url, options));
  };

  useEffect(() => {
    mqttConnect()
  }, []);

  useEffect(() => {
    if (client) {
      client.on('connect', () => {
        setConnectStatus('Connected');
      });
      client.on('error', (err) => {
        console.error('Connection error: ', err);
        client.end();
      });
      client.on('reconnect', () => {
        console.log("/mqtt is Reconnecting/");
        setConnectStatus('Reconnecting');
      });
      client.on('message', (topic, message) => {
        const payload = { topic, message: message.toString() };
        setPayload(payload);
      });
    }
  }, [client]);

  const mqttDisconnect = () => {
    if (client) {
      client.end(() => {
        setConnectStatus('Connect');
      });
    }
  }

  const mqttPublish = (context : publish_context_t) => {
    if (client) {
      const { topic, qos, payload } = context;
      client.publish(topic, payload, { qos }, error => {
        if (error) {
          console.log('Publish error: ', error);
        }
      });
    }
  }

  const mqttSub = (subscription : subscription_t) => {
    if (client) {
      const { topic, qos } = subscription;
      client.subscribe(topic, { qos }, (error) => {
        if (error) {
          console.log('Subscribe to topics error', error)
          return
        }
        setIsSub(true)
      });
    }
  };

  const mqttUnSub = (subscription : subscription_t) => {
    if (client) {
      const { topic } = subscription;
      client.unsubscribe(topic, error => {
        if (error) {
          console.log('Unsubscribe error', error)
          return
        }
        setIsSub(false);
      });
    }
  };

  const mqttContext : mqtt_context_t = {
    isSubed,
    payload,
    connectStatus,
    mqttPublish,
    mqttSub
  }

  return (
    <MqttContext.Provider value={mqttContext}>
      {children}
    </MqttContext.Provider>
  )
};


export default MqttInitializer;
